function isFilled(value) {
    return value != null && `${value}`.trim() !== ''
}

export function isGscccaPayablePackageUuid(uuid) {
    const value = `${uuid || ''}`.trim().toLowerCase()
    if (value === '') return false
    return !value.includes('ecm_1_0')
}

export function isValidGscccaPackageUuid(uuid) {
    const value = `${uuid || ''}`.trim().toLowerCase()
    if (value === '') return false
    return !value.includes('ecm_1_0') && !value.includes('manual_lien_')
}

export function lienSideIsRejected(status) {
    return `${status || ''}`.trim().toLowerCase() === 'rejected'
}

export function lienSideIsRecorded(status) {
    return `${status || ''}`.trim().toLowerCase() === 'recorded'
}

export function lienSideIsManual(status) {
    return `${status || ''}`.trim().toLowerCase() === 'manual'
}

export function lienSideHasApiError(status) {
    const value = `${status || ''}`.trim()
    return value !== '' && /error/i.test(value)
}

export function lienSideHasRetryableProblem(item, side) {
    const status = side === 'patient' ? item?.patient_lien_status : item?.practice_lien_status
    return lienSideIsRejected(status) || lienSideHasApiError(status)
}

export function sideNeedsFullResubmit(item, side) {
    if (lienSideIsRejected(side === 'patient' ? item?.patient_lien_status : item?.practice_lien_status)) {
        return true
    }

    if (!sideHasPackageUuid(item, side)) {
        return false
    }

    return lienSideHasApiError(side === 'patient' ? item?.patient_lien_status : item?.practice_lien_status)
}

export function sideNeedsManualRegenerate(item, side) {
    if (!lienSideIsManual(getLienSideStatus(item, side))) {
        return false
    }

    return !sideHasBookAndPage(item, side)
}

export function sideHasPackageUuid(item, side) {
    const uuid = side === 'patient' ? item?.patient_package_uuid : item?.practice_package_uuid
    return isGscccaPayablePackageUuid(uuid)
}

export function sideHasAnyUuid(item, side) {
    const uuid = side === 'patient' ? item?.patient_package_uuid : item?.practice_package_uuid
    return isFilled(uuid)
}

export function sidePaymentDone(item, side) {
    return side === 'patient' ? Boolean(item?.has_patient_payment) : Boolean(item?.has_practice_payment)
}

export function sideHasBookAndPage(item, side) {
    const book = side === 'patient' ? item?.patient_book : item?.practice_book
    const page = side === 'patient' ? item?.patient_page : item?.practice_page
    return isFilled(book) && isFilled(page)
}

export function sideNeedsPaymentRetry(item, side) {
    if (sideNeedsFullResubmit(item, side)) {
        return false
    }

    if (lienSideIsRecorded(getLienSideStatus(item, side))) {
        return false
    }

    if (!sideHasPackageUuid(item, side)) {
        return false
    }
    return !sidePaymentDone(item, side)
}

export function sidesForTargetType(targetType) {
    if (targetType === 'patient') {
        return ['patient']
    }
    if (targetType === 'practice') {
        return ['practice']
    }
    return ['patient', 'practice']
}

export function lienNeedsPaymentRetryOnly(item, targetType) {
    return sidesForTargetType(targetType).every((side) => sideHasPackageUuid(item, side))
        && sidesForTargetType(targetType).every((side) => sideNeedsPaymentRetry(item, side))
}

export function lienNeedsManualRegenerate(item, targetType) {
    return sidesForTargetType(targetType).some((side) => sideNeedsManualRegenerate(item, side))
}

export function sideIsActiveForRetry(item, side) {
    return isFilled(getLienSideStatus(item, side))
        || sideHasAnyUuid(item, side)
        || sidePaymentDone(item, side)
        || sideHasBookAndPage(item, side)
}

export function sideSuppressesRetry(item, side) {
    const status = getLienSideStatus(item, side)
    if (lienSideIsRejected(status)) {
        return false
    }

    if (sideNeedsManualRegenerate(item, side)) {
        return false
    }

    if (lienSideIsRecorded(status)) {
        return true
    }

    if (sidePaymentDone(item, side)) {
        return true
    }
    if (sideHasBookAndPage(item, side)) {
        return true
    }
    // Real GSCCCA UUID + API Error is a failed filing (including satisfaction rows that
    // still hold the source lien UUID). Do not treat that UUID as a completed filing.
    if (lienSideHasApiError(status) && sideHasPackageUuid(item, side)) {
        return false
    }
    if (sideHasAnyUuid(item, side) && !sideNeedsPaymentRetry(item, side)) {
        return true
    }
    return false
}

export function targetSidesSuppressRetry(item, targetType) {
    const sides = sidesForTargetType(targetType)
    const relevant = targetType === 'both'
        ? sides.filter((side) => sideIsActiveForRetry(item, side))
        : sides
    if (relevant.length === 0) {
        return false
    }
    return relevant.every((side) => sideSuppressesRetry(item, side))
}

export function lienRowHasRecordedStatus(item) {
    return lienSideIsRecorded(item?.patient_lien_status) || lienSideIsRecorded(item?.practice_lien_status)
}

export function shouldShowLienRetryActions({
    item,
    isErroredLien,
    needsPaymentRetryOnly,
    recreateTargetType,
    needsManualRegenerate = false,
}) {
    const wantsRetry = isErroredLien || needsPaymentRetryOnly || needsManualRegenerate
    return wantsRetry && !targetSidesSuppressRetry(item, recreateTargetType)
}

export function getLienSideStatus(item, side) {
    return side === 'patient' ? item?.patient_lien_status : item?.practice_lien_status
}

export function itemHasRejectedSide(item) {
    return lienSideIsRejected(item?.patient_lien_status) || lienSideIsRejected(item?.practice_lien_status)
}

export function lienSideIsReceived(status) {
    return `${status || ''}`.trim().toLowerCase() === 'received'
}

export function sideCanFetchLienStatus(item, side) {
    const uuid = side === 'patient' ? item?.patient_package_uuid : item?.practice_package_uuid
    if (!isValidGscccaPackageUuid(uuid)) {
        return false
    }

    return lienSideIsReceived(getLienSideStatus(item, side))
}
